import { useState, useMemo, useCallback, type Dispatch, type SetStateAction } from 'react';
import type { QuantAnalysisFilter } from '@trinity/chat/api/interfaces/projects.quant-analysis';
import { getDistinctNamesBFS } from '../utils/nested-filter';

const DEFAULT_MAX_VISIBLE = 7;

export type UseQuantAttributeFilterReturn = {
  selectedAttributeNames: Set<string>;
  handleFilterChange: (name: string) => void;
  visibleColumnNames: string[];
  shouldShowToggle: boolean;
  showAllButtons: boolean;
  handleToggleButtons: () => void;
  resetSelectedAttributes: () => void;
};

// Tracks selected attribute tab buttons and keeps quantFilter.columnNames in sync.
// Same-depth selections are unioned, cross-depth selections are intersected.
// e.g. Spravato + Auvelity + Q3'25 → [Spravato, Q3'25] and [Auvelity, Q3'25].
export function useQuantAttributeFilter(
  columnNames: string[][],
  setQuantFilter: Dispatch<SetStateAction<QuantAnalysisFilter>>,
  maxVisible = DEFAULT_MAX_VISIBLE
): UseQuantAttributeFilterReturn {
  const [selectedAttributeNames, setSelectedAttributeNames] = useState<Set<string>>(new Set());
  const [showAllButtons, setShowAllButtons] = useState(false);

  const allFilterColumns = useMemo(() => getDistinctNamesBFS(columnNames), [columnNames]);

  // name → shallowest depth index in column paths
  const nameDepthMap = useMemo(() => {
    const map = new Map<string, number>();
    for (const path of columnNames) {
      for (let depth = 0; depth < path.length; depth++) {
        const name = path[depth];
        if (name != null && !map.has(name)) map.set(name, depth);
      }
    }
    return map;
  }, [columnNames]);

  const handleFilterChange = useCallback(
    (name: string) => {
      const newSelectedNames = new Set(selectedAttributeNames);
      if (newSelectedNames.has(name)) {
        newSelectedNames.delete(name);
      } else {
        newSelectedNames.add(name);
      }
      setSelectedAttributeNames(newSelectedNames);

      let intersectionPaths: string[][] = [];
      if (newSelectedNames.size > 0) {
        // union within depth, then intersect across depths
        const selectedByDepth = new Map<number, Set<string>>();
        for (const n of newSelectedNames) {
          const depth = nameDepthMap.get(n) ?? 0;
          const depthSet = selectedByDepth.get(depth) ?? new Set<string>();
          selectedByDepth.set(depth, depthSet);
          depthSet.add(n);
        }
        intersectionPaths = columnNames.filter((p) =>
          [...selectedByDepth.entries()].every(([depth, names]) => {
            const segment = p[depth];
            return segment != null && names.has(segment);
          })
        );
      }

      setQuantFilter((prev) => ({ ...prev, columnNames: intersectionPaths }));
    },
    [selectedAttributeNames, setQuantFilter, columnNames, nameDepthMap]
  );

  const handleToggleButtons = useCallback(() => {
    setShowAllButtons((prev) => !prev);
  }, []);

  const resetSelectedAttributes = useCallback(() => {
    setSelectedAttributeNames(new Set());
  }, []);

  const shouldShowToggle = allFilterColumns.length > maxVisible;
  const visibleColumnNames = useMemo(
    () => (showAllButtons ? allFilterColumns : allFilterColumns.slice(0, maxVisible)),
    [showAllButtons, allFilterColumns, maxVisible]
  );

  return {
    selectedAttributeNames,
    handleFilterChange,
    visibleColumnNames,
    shouldShowToggle,
    showAllButtons,
    handleToggleButtons,
    resetSelectedAttributes,
  };
}
